import React, { useMemo, useState } from 'react'
import styled from "styled-components"
import { MdInfo, MdOutlinePerson } from "react-icons/md"
import { useModalProvider } from '../../../../../../../Providers/ModalProvider'

import { ModalTitleAndCloseButton } from "../ModalTitleAndCloseButton"

import { MobergButton, MobergButtonShape, MobergButtonVariant } from "../../../../../../../Components/MobergButton/MobergButton"
import { MobergTheme } from "../../../../../../../Components/MobergThemes/MobergColors"
import { MobergFontSize } from "../../../../../../../Components/MobergFont/MobergFont"
import ConfirmationModal from '../../../../../../../Components/ConfirmationModal/ConfirmationModal'
import { Deployment, useEnvironmentVariablesProvider } from "../../../../../../../Providers/EnvironmentVariablesProvider"
import { useNavigate } from 'react-router-dom'
import { FRONTEND_LINKS } from '../../../../../../../Constants/FrontendLinks'
import { FileSelectDropdown } from "../../../../../../../Components/FileSelectDropdown/FileSelectDropdown"
import { PreloadStatusPill } from "../../../../../../../Components/StatusPill/PreloadStatusPill"
import { PreloadDisplayStatus } from "../../../../../../../Components/StatusPill/preloadStatus"
import { PatientFile, getPatientFileId, getPatientFileName, patientFileIsSelectable } from "../../../../../../../Components/FileSelectDropdown/patientFile"
import { parsePrefetchError } from '../../../prefetch/errors'
import { usePrefetchSnapshot, useStartPreload } from '../../../prefetch/hooks'
import { hasEnoughSpaceForPreload } from '../../../prefetch/space'
import {
    ACTIVE_PRELOAD_KINDS,
    buildPreloadStatusMap,
    derivePreloadStatus,
} from '../../../prefetch/selectors'

type SelectPatientFilesModalProps = {
    patientFiles: PatientFile[]
    selectedPatientKey: number
    selectedPatientID: number | string
    selectedSiteName: string
    selectedPatientName?: string
    /** Preselect the newest file when several are available (clinical admitted patients). */
    autoSelectLatestFile?: boolean
}

const getSelectablePatientFiles = (patientFiles: PatientFile[]): PatientFile[] => {
    return patientFiles.filter(file => {
        const fileId = getPatientFileId(file)
        return Boolean(file && getPatientFileName(file) && fileId !== undefined)
    })
}

// patientFiles arrive CNS-first and newest-first, so [0] is the latest CNS recording when one exists.
const getInitialSelectedFileId = (patientFiles: PatientFile[], autoSelectLatestFile: boolean): number | undefined => {
    const selectablePatientFiles = getSelectablePatientFiles(patientFiles)
    if (selectablePatientFiles.length === 1 || (autoSelectLatestFile && selectablePatientFiles.length > 0)) {
        return getPatientFileId(selectablePatientFiles[0])
    }
    return undefined
}

export const SelectPatientFilesModal = (props: SelectPatientFilesModalProps) => {
    const navigate = useNavigate()
    const { close, createModal } = useModalProvider()
    const { deployment } = useEnvironmentVariablesProvider()
    const isClinical = deployment === Deployment.CLINICAL
    const { snapshot, isLoading } = usePrefetchSnapshot()
    const startPreloadMutation = useStartPreload()

    const [selectedFileId, setSelectedFileId] = useState<number | undefined>(() => getInitialSelectedFileId(props.patientFiles, props.autoSelectLatestFile ?? false))
    const statusByDataObjectId = useMemo(
        () => buildPreloadStatusMap(snapshot.files),
        [snapshot.files]
    )

    const filteredPatientFiles = getSelectablePatientFiles(props.patientFiles)
    const selectedPatientFile = filteredPatientFiles.find(file => getPatientFileId(file) === selectedFileId)

    const getPreloadStatus = (file: PatientFile): PreloadDisplayStatus => {
        const fileId = getPatientFileId(file)
        const snapshotStatus = fileId !== undefined ? statusByDataObjectId.get(String(fileId)) : undefined

        if (isLoading && !snapshotStatus) {
            return { kind: "queued", label: "Checking..." }
        }

        return snapshotStatus ?? derivePreloadStatus()
    }

    const isPreloadActiveOrComplete = (file?: PatientFile): boolean => {
        if (!file) {
            return false
        }

        if (isLoading) {
            return false
        }

        return ACTIVE_PRELOAD_KINDS.includes(getPreloadStatus(file).kind)
    }

    const selectedFileHasEnoughSpace = (file?: PatientFile): boolean => hasEnoughSpaceForPreload(
        file?.filesize,
        snapshot.storage.available_for_new_prefetch
    )

    const getPreloadButtonTooltip = (): string | undefined => {
        if (isLoading) {
            return "Checking Hot Storage status..."
        }

        if (selectedFileHasEnoughSpace(selectedPatientFile)) {
            return undefined
        }

        return "Not enough space in Hot Storage to preload this file."
    }

    const patientLabel = props.selectedPatientName || String(props.selectedPatientID)

    function handleSelectPatientFile() {
        if (!selectedPatientFile) {
            return
        }

        const fileName = getPatientFileName(selectedPatientFile)
        const fileId = getPatientFileId(selectedPatientFile)

        navigate(
            `${FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.REVIEW.MAIN}/${props.selectedPatientKey}/${props.selectedPatientID}/${encodeURIComponent(props.selectedSiteName)}/${fileId}/${encodeURIComponent(fileName)}`,
        )
    }

    const handleStartPreload = async () => {
        if (!selectedPatientFile) {
            return
        }

        const fileId = getPatientFileId(selectedPatientFile)

        if (
            fileId === undefined ||
            startPreloadMutation.isLoading ||
            isPreloadActiveOrComplete(selectedPatientFile) ||
            !selectedFileHasEnoughSpace(selectedPatientFile)
        ) {
            return
        }

        try {
            await startPreloadMutation.mutateAsync(fileId)
        } catch (error) {
            console.error("Unable to start preload", error)
            createModal(
                <ConfirmationModal
                    title="There was a problem"
                    description={parsePrefetchError(error)}
                    confirmButton={{ text: "OK", theme: MobergTheme.BLUE }}
                    showCancelButton={false}
                />
            )
        }
    }

    if (filteredPatientFiles.length === 0) {
        return (
            <ConfirmationModal
                title={"No Patient Files"}
                description={"There are no patient files available for review."}
                confirmButton={{ text: "OK", theme: MobergTheme.BLUE }}
                showCancelButton={false}
            />
        )
    }

    return (
        <ModalShell>
            <ModalTitleAndCloseButton title={"Select a Patient File"} closeFunction={close} />
            <ModalBody>
                <PatientSummary>
                    <MdOutlinePerson size={16} />
                    <span>{patientLabel}</span>
                    <SeparatorDot>•</SeparatorDot>
                    <span>{filteredPatientFiles.length} {filteredPatientFiles.length === 1 ? "file" : "files"} available</span>
                </PatientSummary>
                <FieldGroup>
                    <FieldLabel>Select a file for review</FieldLabel>
                    <FileSelectDropdown
                        files={filteredPatientFiles}
                        value={selectedFileId}
                        onChange={file => setSelectedFileId(getPatientFileId(file))}
                        width={"100%"}
                        isClinical={isClinical}
                        statusFor={isClinical ? undefined : file => <PreloadStatusPill status={getPreloadStatus(file)} />}
                    />
                    {!isClinical && (
                        <HelpMessage>
                            <MdInfo size={18} color="var(--color-accent-interactive)" />
                            <span>Preloading moves the file into hot storage for faster review.</span>
                        </HelpMessage>
                    )}
                </FieldGroup>
                <FooterRow>
                    {!isClinical && selectedPatientFile && patientFileIsSelectable(selectedPatientFile) && !isPreloadActiveOrComplete(selectedPatientFile) && (
                        <MobergButton
                            onClick={handleStartPreload}
                            disabled={
                                startPreloadMutation.isLoading ||
                                isLoading ||
                                !selectedFileHasEnoughSpace(selectedPatientFile)
                            }
                            theme={MobergTheme.BLUE}
                            variant={MobergButtonVariant.OUTLINE}
                            fontSize={MobergFontSize.LARGE}
                            shape={MobergButtonShape.WIDE}
                            tooltip={getPreloadButtonTooltip()}
                            style={{ minWidth: "140px" }}>
                            {startPreloadMutation.isLoading ? "Starting..." : "Start preload"}
                        </MobergButton>
                    )}
                    <MobergButton
                        onClick={handleSelectPatientFile}
                        disabled={!selectedPatientFile || !patientFileIsSelectable(selectedPatientFile)}
                        theme={MobergTheme.BLUE}
                        variant={MobergButtonVariant.FILLED}
                        fontSize={MobergFontSize.LARGE}
                        shape={MobergButtonShape.WIDE}
                        style={{ minWidth: "120px" }}>
                        View file
                    </MobergButton>
                </FooterRow>
            </ModalBody>
        </ModalShell>
    )
}

const ModalShell = styled.div`
    width: 600px;
    padding: 0;
    display: flex;
    flex-direction: column;
`

const ModalBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 18px 24px 22px;
    width: 100%;
`

const PatientSummary = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--color-text-primary);
    font-size: ${MobergFontSize.REGULAR};
    font-weight: 500;
`

const SeparatorDot = styled.span`
    color: var(--text-secondary-color);
`

const FieldGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`

const FieldLabel = styled.div`
    color: var(--color-text-primary);
    font-size: ${MobergFontSize.REGULAR};
    font-weight: 500;
`

const HelpMessage = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: start;
    gap: 10px;
    border-radius: 4px;
    background: var(--color-hover-neutral);
    color: var(--color-text-primary);
    font-size: ${MobergFontSize.SMALL};
    line-height: 1.45;
    padding: 8px 12px;
    margin-top: 5px;
`

const FooterRow = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    margin-top: 6px;
`
